import { create } from 'zustand';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, increment, Timestamp, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';
import { PlayerStats, PlayerStyleMetrics, PlayerVsPlayer, GameRecord } from '../types';
import { generatePlayerStyle } from '../utils/playerStyle';
import { processFinishedGame } from '../services/gameStatsService';

import { useAuthStore } from './useAuthStore';

interface StatsState {
  globalStats: PlayerStats | null;
  globalStatsUserId: string | null;
  styleMetrics: PlayerStyleMetrics | null;
  styleMetricsUserId: string | null;
  playerStyle: string[];
  loading: boolean;
  
  fetchGlobalStats: (userId: string) => Promise<PlayerStats | null>;
  fetchStyleMetrics: (userId: string) => Promise<PlayerStyleMetrics | null>;
  fetchVsStats: (userId: string, opponentId: string) => Promise<PlayerVsPlayer | null>;
  fetchModeStats: (userId: string, modeId: string) => Promise<PlayerStats | null>;
  fetchDeveloperAnalysis: (gameId: string) => Promise<any | null>;
  
  processGame: (game: GameRecord) => Promise<void>;
  resetStats: (userId: string) => Promise<void>;
}

const initialStats: PlayerStats = {
  gamesPlayed: 0,
  gamesFinished: 0,
  gamesAbandoned: 0,
  gamesTimeout: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  winRate: 0,
  totalScore: 0,
  averageScore: 0,
  bestScore: 0,
  worstScore: 0,
  totalWords: 0,
  averageWordScore: 0,
  bestWordScore: 0,
  bestWord: '',
  totalTurns: 0,
  averageTurnTime: 0,
  fastestTurnTime: 0,
  slowestTurnTime: 0,
  moves50plus: 0,
  moves70plus: 0,
  moves100plus: 0,
  fullRackUses: 0,
  swapsCount: 0,
  passCount: 0,
  longestWordLength: 0,
  averageWordLength: 0,
  streakCurrent: 0,
  streakBest: 0,
  uniqueOpponents: 0,
  lastPlayDate: Timestamp.now(),
  daysActive: 0,
  uniqueBoards: [],
  uniqueLanguages: [],
  robotWins: {},
  robotGames: 0,
  daysActiveStreak: 0,
  socialInteractions: 0
};

const initialStyle: PlayerStyleMetrics = {
  aggressionIndex: 0.5,
  boardOpeningRate: 0.5,
  blockingRate: 0.5,
  premiumUsageRate: 0.5,
  longWordRate: 0.5,
  shortWordRate: 0.5,
  rackChangeRate: 0.5,
  riskTakingIndex: 0.5,
  comebackRate: 0,
  endgameEfficiency: 0.5,
  averageTurnTime: 30,
  bingoRate: 0,
  swapRate: 0,
  passRate: 0,
  vowelConsonantBalance: 1,
  averageWordScore: 0
};

export const useStatsStore = create<StatsState>((set, get) => ({
  globalStats: null,
  globalStatsUserId: null,
  styleMetrics: null,
  styleMetricsUserId: null,
  playerStyle: [],
  loading: false,

  fetchGlobalStats: async (userId) => {
    const { globalStats, globalStatsUserId } = get();
    // Use cached stats only when they belong to the requested player.
    if (globalStats && globalStatsUserId === userId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[StatsStore] Using cached global stats for', userId);
      }
      return globalStats;
    }

    set({ loading: true });
    try {
      const statsRef = doc(db, 'users', userId, 'stats', 'global');
      const snap = await getDoc(statsRef);
      if (snap.exists()) {
        const data = snap.data() as PlayerStats;
        set({ globalStats: data, globalStatsUserId: userId, loading: false });
        return data;
      }
      set({ globalStats: initialStats, globalStatsUserId: userId, loading: false });
      return initialStats;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${userId}/stats/global`);
      set({ loading: false });
      return null;
    }
  },

  fetchStyleMetrics: async (userId) => {
    const { styleMetrics, styleMetricsUserId } = get();
    // Use cached style only when it belongs to the requested player.
    if (styleMetrics && styleMetricsUserId === userId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[StatsStore] Using cached style metrics for', userId);
      }
      return styleMetrics;
    }

    try {
      const styleRef = doc(db, 'users', userId, 'stats', 'style');
      const snap = await getDoc(styleRef);
      if (snap.exists()) {
        const data = snap.data() as PlayerStyleMetrics;
        set({ styleMetrics: data, styleMetricsUserId: userId, playerStyle: generatePlayerStyle(data) });
        return data;
      }
      set({ styleMetrics: initialStyle, styleMetricsUserId: userId, playerStyle: [] });
      return initialStyle;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${userId}/stats/style`);
      return null;
    }
  },

  fetchVsStats: async (userId, opponentId) => {
    try {
      const pairKey = [userId, opponentId].sort().join('_');
      const h2hRef = doc(db, 'headToHead', pairKey);
      const snap = await getDoc(h2hRef);
      
      if (snap.exists()) {
        const data = snap.data();
        const myStats = data[`${userId}_stats`];
        if (myStats) {
          return {
            opponentId,
            gamesPlayed: myStats.gamesPlayed || 0,
            wins: myStats.wins || 0,
            losses: myStats.losses || 0,
            draws: myStats.draws || 0,
            totalScore: myStats.pointsFor || 0,
            opponentTotalScore: myStats.pointsAgainst || 0,
            lastPlayed: myStats.lastPlayedAt || Timestamp.now()
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching vs stats:", error);
      return null;
    }
  },

  fetchModeStats: async (userId, modeId) => {
    try {
      const modeRef = doc(db, 'users', userId, 'stats', 'mode_' + modeId);
      const snap = await getDoc(modeRef);
      if (snap.exists()) return snap.data() as PlayerStats;
      return initialStats;
    } catch (error) {
      console.error("Error fetching mode stats:", error);
      return null;
    }
  },

  fetchDeveloperAnalysis: async (gameId) => {
    try {
      const analysisRef = doc(db, 'games', gameId, 'analysis', 'developer');
      const snap = await getDoc(analysisRef);
      if (snap.exists()) return snap.data();
      return null;
    } catch (error) {
      console.error("Error fetching developer analysis:", error);
      return null;
    }
  },

  processGame: async (game) => {
    try {
      await processFinishedGame(game);
      // Refresh stats after processing
      const user = useAuthStore.getState().user;
      if (user) {
        await get().fetchGlobalStats(user.uid);
        await get().fetchStyleMetrics(user.uid);
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `games/${game.id}/process`);
    }
  },

  resetStats: async (userId: string) => {
    set({ loading: true });
    try {
      // 1. Reset Global Stats
      const globalRef = doc(db, 'users', userId, 'stats', 'global');
      await setDoc(globalRef, { ...initialStats, lastPlayDate: Timestamp.now() });

      // 2. Reset Style Metrics
      const styleRef = doc(db, 'users', userId, 'stats', 'style');
      await setDoc(styleRef, initialStyle);

      // 3. Find and delete all mode-specific stats
      const statsColl = collection(db, 'users', userId, 'stats');
      const snap = await getDocs(statsColl);
      
      const deletePromises = snap.docs
        .filter(d => d.id !== 'global' && d.id !== 'style')
        .map(d => deleteDoc(d.ref));
      
      await Promise.all(deletePromises);

      // 4. Update local state
      set({ 
        globalStats: initialStats, 
        globalStatsUserId: userId,
        styleMetrics: initialStyle, 
        styleMetricsUserId: userId,
        playerStyle: [],
        loading: false 
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${userId}/stats/reset`);
      set({ loading: false });
      throw error;
    }
  }
}));
